import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { bffApiClient } from '@api/bff/client';
import {
  useGlobalVariable,
  useUpdateGlobalVariable,
  useCreateGlobalVariable
} from '@api/enterprise/globalVariable';
import { globalVariablesKeys } from '@api/enterprise/globalVariable/constants';
import { useApiToast } from '@hooks/useApiToast';
import { useMqttStore } from '@stores/mqttStore';
import type { Robot, StationDevicesConfig } from './types';

const GLOBAL_VAR_NAME = 'HIDE_STATION_DEVICES';
const DEFAULT_STATION_ID =
  import.meta.env.VITE_MQTT_STATION_ID || 'DEV-STATION-01';

const EMPTY_CONFIG: StationDevicesConfig = {
  activeStationId: null,
  robots: []
};

const generateId = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `robot-${Date.now()}-${Math.floor(Math.random() * 1_000_000)}`;

const toRobot = (value: Partial<Robot>): Robot => ({
  id: value.id || generateId(),
  label: value.label ?? '',
  stationId: value.stationId ?? '',
  mqttDeviceId: value.mqttDeviceId ?? '',
  deviceType: value.deviceType ?? '',
  setupSteps: value.setupSteps ?? [],
  teardownSteps: value.teardownSteps ?? []
});

const parseConfig = (blobText?: string | null): StationDevicesConfig => {
  if (!blobText) return EMPTY_CONFIG;
  try {
    const parsed = JSON.parse(blobText);

    if (parsed && Array.isArray(parsed.robots)) {
      const robots = parsed.robots.map(toRobot);
      const activeStationId =
        parsed.activeStationId ?? robots[0]?.stationId ?? null;
      return { activeStationId, robots };
    }

    if (parsed && (parsed.mqttDeviceId || parsed.deviceType || parsed.label)) {
      const robot = toRobot({
        ...parsed,
        stationId: parsed.stationId || DEFAULT_STATION_ID
      });
      return { activeStationId: robot.stationId, robots: [robot] };
    }

    return EMPTY_CONFIG;
  } catch {
    return EMPTY_CONFIG;
  }
};

const toBffPayload = (config: StationDevicesConfig) => ({
  activeStationId: config.activeStationId,
  robots: config.robots.map(robot => ({
    stationId: robot.stationId,
    mqttDeviceId: robot.mqttDeviceId,
    deviceType: robot.deviceType,
    label: robot.label,
    setupSteps: robot.setupSteps,
    teardownSteps: robot.teardownSteps
  }))
});

/**
 * Reads the registered robots and the active station from the global variable.
 */
export const useStationDevicesQuery = () => {
  const {
    data: globalVar,
    isLoading,
    error
  } = useGlobalVariable(GLOBAL_VAR_NAME);

  const blobText = globalVar?.blobText;
  const config = useMemo(() => parseConfig(blobText), [blobText]);

  return {
    robots: config.robots,
    activeStationId: config.activeStationId,
    isLoading,
    error
  };
};

const usePersistConfig = () => {
  const { data: globalVar } = useGlobalVariable(GLOBAL_VAR_NAME);
  const updateGlobalVar = useUpdateGlobalVariable();
  const createGlobalVar = useCreateGlobalVariable();

  const current = useMemo(
    () => parseConfig(globalVar?.blobText),
    [globalVar?.blobText]
  );

  const persist = async (next: StationDevicesConfig) => {
    const blobText = JSON.stringify(next);
    if (globalVar) {
      await updateGlobalVar.mutateAsync({
        name: GLOBAL_VAR_NAME,
        command: { blobText }
      });
    } else {
      await createGlobalVar.mutateAsync({ name: GLOBAL_VAR_NAME, blobText });
    }
    await bffApiClient.put('/api/station-device-config', toBffPayload(next));
  };

  return { current, persist };
};

/**
 * Adds or updates a single robot (matched by id) in the registered list.
 */
export const useUpsertStationDevice = () => {
  const queryClient = useQueryClient();
  const { current, persist } = usePersistConfig();
  const { showSuccess, showError } = useApiToast();

  return useMutation<Robot, Error, Robot>({
    mutationFn: async robot => {
      const saved = toRobot(robot);
      const exists = current.robots.some(r => r.id === saved.id);
      const robots = exists
        ? current.robots.map(r => (r.id === saved.id ? saved : r))
        : [...current.robots, saved];
      const activeStationId = current.activeStationId ?? saved.stationId;
      await persist({ activeStationId, robots });
      return saved;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: globalVariablesKeys.byName(GLOBAL_VAR_NAME)
      });
      showSuccess('stationDevices.toast.saveSuccess', 'settings');
    },
    onError: error => {
      showError('stationDevices.toast.saveError', error, 'settings');
    }
  });
};

/**
 * Removes a robot by id, reassigning the active station when the removed robot was active.
 */
export const useDeleteRobot = () => {
  const queryClient = useQueryClient();
  const { current, persist } = usePersistConfig();
  const { showSuccess, showError } = useApiToast();

  return useMutation<void, Error, string>({
    mutationFn: async id => {
      const removed = current.robots.find(r => r.id === id);
      const robots = current.robots.filter(r => r.id !== id);
      const activeStationId =
        removed && removed.stationId === current.activeStationId
          ? robots[0]?.stationId ?? null
          : current.activeStationId;
      await persist({ activeStationId, robots });
      if (activeStationId) {
        useMqttStore.getState().setStationId(activeStationId);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: globalVariablesKeys.byName(GLOBAL_VAR_NAME)
      });
      showSuccess('stationDevices.toast.deleteSuccess', 'settings');
    },
    onError: error => {
      showError('stationDevices.toast.deleteError', error, 'settings');
    }
  });
};

/**
 * Marks a station active, persists it, and re-points the live MQTT connection.
 */
export const useSetActiveRobot = () => {
  const queryClient = useQueryClient();
  const { current, persist } = usePersistConfig();
  const { showError } = useApiToast();

  return useMutation<void, Error, string>({
    mutationFn: async stationId => {
      await persist({ ...current, activeStationId: stationId });
      useMqttStore.getState().setStationId(stationId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: globalVariablesKeys.byName(GLOBAL_VAR_NAME)
      });
    },
    onError: error => {
      showError('stationDevices.toast.saveError', error, 'settings');
    }
  });
};

/**
 * Pushes the entire robot list + active station to the BFF in-memory store.
 * Runs at app startup so orchestration can resolve any robot by stationId.
 */
export const useSyncStationDeviceToBff = () => {
  const { data: globalVar } = useGlobalVariable(GLOBAL_VAR_NAME);

  return useQuery({
    queryKey: ['stationDevices', 'bff-sync'],
    queryFn: async () => {
      const config = parseConfig(globalVar?.blobText);
      await bffApiClient.put(
        '/api/station-device-config',
        toBffPayload(config)
      );
      return config;
    },
    enabled: !!globalVar,
    staleTime: Infinity
  });
};
